import * as React from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  AI_ASSISTANT_ENTITIES,
  AI_CRUD_ACTIONS,
  type AiAssistantSettings,
  type AiLlmProvider,
  type AiPurposeModel,
  type UpdateAiAssistantSettingsRequest,
} from "@/types/api";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";

/** Per-purpose model slots surfaced in the UI. Empty model = inherit global. */
const AI_MODEL_PURPOSES = [
  {
    key: "email_reply",
    labelKey: "aiAssistant.settings.purposes.emailReply.label",
    descriptionKey: "aiAssistant.settings.purposes.emailReply.description",
  },
  {
    key: "email_translate",
    labelKey: "aiAssistant.settings.purposes.emailTranslate.label",
    descriptionKey: "aiAssistant.settings.purposes.emailTranslate.description",
  },
] as const;

type PurposeForm = {
  provider: AiLlmProvider;
  model: string;
  temperature: string;
  maxOutputTokens: string;
};

interface FormState {
  enabled: boolean;
  provider: AiLlmProvider;
  model: string;
  temperature: number;
  contextMessageLimit: number;
  entityPermissions: Record<string, Record<string, boolean>>;
  purposeModels: Record<string, PurposeForm>;
}

function dtoToForm(s: AiAssistantSettings): FormState {
  const perms: Record<string, Record<string, boolean>> = {};
  for (const e of AI_ASSISTANT_ENTITIES) {
    perms[e] = {};
    for (const a of AI_CRUD_ACTIONS) {
      perms[e][a] = Boolean(s.entityPermissions?.[e]?.[a]);
    }
  }
  const purposeModels: Record<string, PurposeForm> = {};
  for (const p of AI_MODEL_PURPOSES) {
    const existing = s.purposeModels?.[p.key];
    purposeModels[p.key] = {
      provider: existing?.provider ?? s.provider,
      model: existing?.model ?? "",
      temperature:
        existing?.temperature !== undefined ? String(existing.temperature) : "",
      maxOutputTokens:
        existing?.maxOutputTokens !== undefined
          ? String(existing.maxOutputTokens)
          : "",
    };
  }
  return {
    enabled: s.enabled,
    provider: s.provider,
    model: s.model,
    temperature: s.temperature,
    contextMessageLimit: s.contextMessageLimit,
    entityPermissions: perms,
    purposeModels,
  };
}

/** Build the API payload: only purposes with a non-empty model are sent. */
function formPurposesToPayload(
  purposeModels: Record<string, PurposeForm>
): Record<string, AiPurposeModel> {
  const out: Record<string, AiPurposeModel> = {};
  for (const [key, v] of Object.entries(purposeModels)) {
    const model = v.model.trim();
    if (!model) continue;
    const temp = v.temperature.trim();
    const maxTok = v.maxOutputTokens.trim();
    out[key] = {
      provider: v.provider,
      model,
      ...(temp ? { temperature: parseFloat(temp) } : {}),
      ...(maxTok ? { maxOutputTokens: parseInt(maxTok, 10) } : {}),
    };
  }
  return out;
}

export function AiAssistantSettingsPage() {
  const qc = useQueryClient();
  const { t } = useTranslation("templates");
  const { data, isLoading, error } = useQuery({
    queryKey: ["ai-settings"],
    queryFn: () => api.get<{ settings: AiAssistantSettings }>("/api/admin/ai-settings"),
  });

  const [form, setForm] = React.useState<FormState | null>(null);

  React.useEffect(() => {
    if (data?.settings) setForm(dtoToForm(data.settings));
  }, [data]);

  const mutation = useMutation({
    mutationFn: (body: UpdateAiAssistantSettingsRequest) =>
      api.put<{ settings: AiAssistantSettings }>("/api/admin/ai-settings", body),
    onSuccess: (res) => {
      toast.success(t("aiAssistant.settings.saved"));
      qc.setQueryData(["ai-settings"], res);
      setForm(dtoToForm(res.settings));
    },
    onError: (err) => {
      toast.error(
        t("errors.saveFailed", { message: err instanceof Error ? err.message : String(err) })
      );
    },
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Link to="/templates" className="hover:underline">{t("list.title")}</Link>
        <span>/</span>
        <Link to="/templates/ai-assistant" className="hover:underline">
          {t("aiAssistant.breadcrumb")}
        </Link>
        <span>/</span>
        <span>{t("aiAssistant.modelSettings")}</span>
      </div>
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">{t("aiAssistant.settings.title")}</h1>
        <p className="text-sm text-muted-foreground">
          {t("aiAssistant.settings.description")}
        </p>
      </div>

      {isLoading || !form ? (
        <Skeleton className="h-96 w-full" />
      ) : error ? (
        <p className="text-sm text-destructive">
          {t("errors.loadFailed", {
            message: error instanceof Error ? error.message : String(error),
          })}
        </p>
      ) : (
        <>
          <Card>
            <CardHeader>
              <CardTitle>{t("aiAssistant.settings.general.title")}</CardTitle>
              <CardDescription>{t("aiAssistant.settings.general.description")}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>{t("aiAssistant.settings.general.enabled")}</Label>
                  <p className="text-xs text-muted-foreground">
                    {t("aiAssistant.settings.general.enabledHint")}
                  </p>
                </div>
                <Switch
                  checked={form.enabled}
                  onCheckedChange={(v) => setForm({ ...form, enabled: v })}
                />
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="provider">{t("aiAssistant.settings.general.provider")}</Label>
                  <Select
                    value={form.provider}
                    onValueChange={(v) => setForm({ ...form, provider: v as AiLlmProvider })}
                  >
                    <SelectTrigger id="provider"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="openai">openai</SelectItem>
                      <SelectItem value="anthropic">anthropic</SelectItem>
                      <SelectItem value="gemini">gemini</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="model">{t("aiAssistant.settings.general.model")}</Label>
                  <Input
                    id="model"
                    value={form.model}
                    onChange={(e) => setForm({ ...form, model: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="temperature">{t("aiAssistant.settings.general.temperature")}</Label>
                  <Input
                    id="temperature"
                    type="number"
                    min={0}
                    max={2}
                    step={0.01}
                    value={form.temperature}
                    onChange={(e) => setForm({ ...form, temperature: parseFloat(e.target.value) })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ctx">{t("aiAssistant.settings.general.contextLimit")}</Label>
                  <Input
                    id="ctx"
                    type="number"
                    min={2}
                    max={200}
                    value={form.contextMessageLimit}
                    onChange={(e) =>
                      setForm({ ...form, contextMessageLimit: parseInt(e.target.value, 10) || 0 })
                    }
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{t("aiAssistant.settings.purposes.title")}</CardTitle>
              <CardDescription>
                {t("aiAssistant.settings.purposes.description")}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {AI_MODEL_PURPOSES.map((p) => {
                const pm = form.purposeModels[p.key];
                return (
                  <div key={p.key} className="space-y-2 rounded-md border p-3">
                    <div className="space-y-0.5">
                      <Label className="font-medium">{t(p.labelKey)}</Label>
                      <p className="text-xs text-muted-foreground">{t(p.descriptionKey)}</p>
                    </div>
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
                      <div className="space-y-1">
                        <Label className="text-xs">{t("aiAssistant.settings.purposes.provider")}</Label>
                        <Select
                          value={pm.provider}
                          onValueChange={(v) => {
                            const next = { ...form.purposeModels };
                            next[p.key] = { ...pm, provider: v as AiLlmProvider };
                            setForm({ ...form, purposeModels: next });
                          }}
                        >
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="openai">openai</SelectItem>
                            <SelectItem value="anthropic">anthropic</SelectItem>
                            <SelectItem value="gemini">gemini</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">{t("aiAssistant.settings.purposes.model")}</Label>
                        <Input
                          value={pm.model}
                          placeholder={form.model}
                          onChange={(e) => {
                            const next = { ...form.purposeModels };
                            next[p.key] = { ...pm, model: e.target.value };
                            setForm({ ...form, purposeModels: next });
                          }}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">{t("aiAssistant.settings.purposes.temperature")}</Label>
                        <Input
                          type="number"
                          min={0}
                          max={2}
                          step={0.01}
                          value={pm.temperature}
                          placeholder={String(form.temperature)}
                          onChange={(e) => {
                            const next = { ...form.purposeModels };
                            next[p.key] = { ...pm, temperature: e.target.value };
                            setForm({ ...form, purposeModels: next });
                          }}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">
                          {t("aiAssistant.settings.purposes.maxOutputTokens")}
                        </Label>
                        <Input
                          type="number"
                          min={1}
                          max={32000}
                          step={1}
                          value={pm.maxOutputTokens}
                          placeholder={t("aiAssistant.settings.purposes.providerDefault")}
                          onChange={(e) => {
                            const next = { ...form.purposeModels };
                            next[p.key] = { ...pm, maxOutputTokens: e.target.value };
                            setForm({ ...form, purposeModels: next });
                          }}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{t("aiAssistant.settings.permissions.title")}</CardTitle>
              <CardDescription>
                {t("aiAssistant.settings.permissions.description")}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                      <th className="py-2 pr-4">{t("aiAssistant.settings.permissions.entity")}</th>
                      {AI_CRUD_ACTIONS.map((a) => (
                        <th key={a} className="px-2 py-2 text-center">{a}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {AI_ASSISTANT_ENTITIES.map((e) => (
                      <tr key={e} className="border-t">
                        <td className="py-2 pr-4 font-medium">{e}</td>
                        {AI_CRUD_ACTIONS.map((a) => (
                          <td key={a} className="px-2 py-2 text-center">
                            <input
                              type="checkbox"
                              className="h-4 w-4"
                              checked={form.entityPermissions[e]?.[a] ?? false}
                              onChange={(ev) => {
                                const next = { ...form.entityPermissions };
                                next[e] = { ...next[e], [a]: ev.target.checked };
                                setForm({ ...form, entityPermissions: next });
                              }}
                            />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          <div className="flex items-center gap-2">
            <Button
              onClick={() =>
                mutation.mutate({
                  enabled: form.enabled,
                  provider: form.provider,
                  model: form.model,
                  temperature: form.temperature,
                  contextMessageLimit: form.contextMessageLimit,
                  entityPermissions: form.entityPermissions,
                  purposeModels: formPurposesToPayload(form.purposeModels),
                })
              }
              disabled={mutation.isPending}
            >
              {mutation.isPending ? t("actions.saving") : t("aiAssistant.settings.saveChanges")}
            </Button>
            <Button
              variant="outline"
              onClick={() => data?.settings && setForm(dtoToForm(data.settings))}
              disabled={mutation.isPending}
            >
              {t("aiAssistant.settings.reset")}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
